<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<button
						type="button"
						class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 border-none text-gray-700 cursor-pointer transition-colors hover:bg-gray-200"
						title="Back to Photoframes"
						@click="goBack"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
					</button>
					<div>
						<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Photoframe</h1>
						<p class="text-sm text-gray-500 mt-1 mb-0">ID: {{ routeId }}</p>
					</div>
				</div>
				<!-- Desktop Save Button -->
				<button
					type="button"
					class="save-btn-desktop inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
					:disabled="saving"
					@click="saveItem"
				>
					<svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ saving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Loading State -->
			<div v-if="loading" class="flex justify-center items-center py-16 px-4">
				<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span class="ml-3 text-gray-500 text-sm">Loading photoframe...</span>
			</div>

			<!-- Form Card -->
			<div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
				<!-- Card Header -->
				<div class="py-4 px-6 border-b border-gray-200 flex items-center gap-3">
					<svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
					</svg>
					<h2 class="text-lg font-semibold text-gray-900 m-0">Photoframe Properties</h2>
					<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
						ID: {{ routeId }}
					</span>
				</div>

				<!-- Form Fields -->
				<div class="p-6">
					<div class="grid gap-6 max-w-3xl">
						<!-- Image Section -->
						<div class="col-span-2">
							<label class="block text-sm font-medium text-gray-700 mb-2">
								Source Image
							</label>
							<div class="flex items-start gap-4 flex-wrap">
								<div class="w-40 h-40 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
									<img
										v-if="item.source"
										:src="item.source"
										alt="Photoframe image"
										class="w-full h-full object-contain"
									>
									<svg v-else class="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
										<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
									</svg>
								</div>
								<div class="flex flex-col gap-2">
									<button
										type="button"
										class="py-2 px-4 text-sm font-medium text-orange-7 bg-transparent border border-orange-4 rounded-lg cursor-pointer transition-colors hover:bg-orange-1"
										@click="uploadImage"
									>
										Upload Image
									</button>
									<button
										v-if="item.source"
										type="button"
										class="py-2 px-4 text-sm font-medium text-red-600 bg-transparent border border-red-200 rounded-lg cursor-pointer transition-colors hover:bg-red-50"
										@click="removeImage"
									>
										Remove Image
									</button>
								</div>
							</div>
						</div>

						<!-- Name Field -->
						<div>
							<label
								for="name"
								class="block text-sm font-medium text-gray-700 mb-2"
							>
								Name
							</label>
							<input
								id="name"
								v-model="item.name"
								type="text"
								placeholder="Enter photoframe name"
								class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
							>
						</div>

						<!-- Scaling Field -->
						<div>
							<label
								for="scaling"
								class="block text-sm font-medium text-gray-700 mb-2"
							>
								Scaling
							</label>
							<input
								id="scaling"
								v-model.number="item.scaling"
								type="number"
								step="0.01"
								placeholder="1.0"
								class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
							>
						</div>

						<!-- Dimensions Section -->
						<div class="col-span-2">
							<h3 class="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
								Dimensions
							</h3>
							<div class="grid grid-cols-2 gap-4">
								<div>
									<label
										for="width"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Width
									</label>
									<input
										id="width"
										v-model.number="item.width"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
								<div>
									<label
										for="height"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Height
									</label>
									<input
										id="height"
										v-model.number="item.height"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
							</div>
						</div>

						<!-- Border Widths Section -->
						<div class="col-span-2">
							<h3 class="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
								Border Widths
							</h3>
							<div class="grid grid-cols-2 gap-4">
								<div>
									<label
										for="borderwidth_top"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Top
									</label>
									<input
										id="borderwidth_top"
										v-model.number="item.borderwidth_top"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
								<div>
									<label
										for="borderwidth_right"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Right
									</label>
									<input
										id="borderwidth_right"
										v-model.number="item.borderwidth_right"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
								<div>
									<label
										for="borderwidth_bottom"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Bottom
									</label>
									<input
										id="borderwidth_bottom"
										v-model.number="item.borderwidth_bottom"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
								<div>
									<label
										for="borderwidth_left"
										class="block text-sm font-medium text-gray-700 mb-2"
									>
										Left
									</label>
									<input
										id="borderwidth_left"
										v-model.number="item.borderwidth_left"
										type="number"
										placeholder="0"
										class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
								</div>
							</div>
						</div>

						<!-- Mobile Save Button -->
						<div class="save-btn-mobile col-span-2">
							<button
								type="button"
								class="w-full inline-flex items-center justify-center gap-2 bg-orange-7 text-white py-3 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
								:disabled="saving"
								@click="saveItem"
							>
								<svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
								<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
								</svg>
								{{ saving ? 'Saving...' : 'Save Changes' }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import { PickerOptions, PickerResponse } from 'filestack-js';
import OfferingFrameService from 'services/OfferingFrameService';

export default defineComponent({
	name: 'EditPhotoframePage',
	setup() {
		const router = useRouter();
		const route = useRoute();
		const instance = getCurrentInstance();

		const routeId = computed(() => route.params.id as string);
		const loading = ref(true);
		const saving = ref(false);
		const item = ref<any>({});

		const goBack = () => {
			router.push('/photoframes');
		};

		const fetchItem = async () => {
			loading.value = true;
			try {
				const data = await OfferingFrameService.get(routeId.value);
				item.value = data;
			} catch (error: any) {
				console.error('Failed to load photoframe:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load photoframe',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				loading.value = false;
			}
		};

		const uploadImage = () => {
			const filestackClient = instance?.appContext.config.globalProperties.$filestack;
			if (!filestackClient) return;

			const options: PickerOptions = {
				accept: ['image/*'],
				maxFiles: 1,
				onUploadDone: (result: PickerResponse) => {
					if (result.filesUploaded.length > 0) {
						item.value.source = result.filesUploaded[0].url;
					}
				},
			};
			filestackClient.picker(options).open();
		};

		const removeImage = () => {
			item.value.source = '';
		};

		const saveItem = async () => {
			saving.value = true;
			try {
				await OfferingFrameService.update(routeId.value, item.value);
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Photoframe saved',
					showConfirmButton: false,
					timer: 2000,
					timerProgressBar: true,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save photoframe',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				saving.value = false;
			}
		};

		onMounted(() => {
			fetchItem();
		});

		return {
			routeId,
			loading,
			saving,
			item,
			goBack,
			uploadImage,
			removeImage,
			saveItem,
		};
	},
});
</script>

<style scoped>
@media (max-width: 640px) {
	.save-btn-desktop {
		display: none !important;
	}
	.save-btn-mobile {
		display: block !important;
	}
}
</style>
